import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from '../components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Combobox } from '../components/Combobox';
import { ESTADOS } from '../components/StateCitySelect';
import cidadesBrasil from '../data/cidades-brasil.json';
import { useAuth } from '../contexts/AuthContext';
import {
  PiggyBank, Plus, Trash2, Save, AlertCircle, CheckCircle2, RotateCcw,
  CalendarDays, Layers, MapPin, X, ChevronDown, ChevronUp,
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fmtBRL = (n) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(n || 0);

const newId = () => Math.random().toString(36).slice(2, 10);

const sumPct = (arr, key = 'percentual') =>
  Math.round(arr.reduce((acc, x) => acc + (Number(x[key]) || 0), 0) * 100) / 100;

const StatusBadge = ({ value, tolerance = 0.01 }) => {
  const diff = Math.abs((value || 0) - 100);
  let cls = 'bg-amber-100 text-amber-700 border-amber-200';
  let label = `${value || 0}%`;
  if (diff <= tolerance) {
    cls = 'bg-emerald-100 text-emerald-700 border-emerald-200';
    label = `${value}% ✓`;
  } else if ((value || 0) > 100 + tolerance) {
    cls = 'bg-red-100 text-red-700 border-red-200';
  }
  return (
    <Badge variant="outline" className={`${cls} font-bold`}>
      {label}
    </Badge>
  );
};

const ProgressBar = ({ value }) => {
  const pct = Math.min(Math.max(value || 0, 0), 150);
  const diff = Math.abs((value || 0) - 100);
  let bar = 'bg-amber-500';
  if (diff <= 0.01) bar = 'bg-emerald-500';
  else if ((value || 0) > 100.01) bar = 'bg-red-500';
  return (
    <div className="h-2 w-full rounded-full bg-slate-100 overflow-hidden">
      <div
        className={`h-full ${bar} transition-all`}
        style={{ width: `${Math.min(pct, 100)}%` }}
      />
    </div>
  );
};

export const FinanceiroPlanejamentoPage = () => {
  const { user } = useAuth();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedArea, setExpandedArea] = useState(null);

  // Per-group draft state for the "add city" form (estado/cidade currently being picked)
  const [draftSelections, setDraftSelections] = useState({});

  const allowedStates = useMemo(() => {
    const list = user?.active_tenant?.allowed_states;
    return Array.isArray(list) && list.length > 0 ? list : null;
  }, [user]);

  const estadosVisiveis = useMemo(() => {
    if (!allowedStates) return ESTADOS;
    const set = new Set(allowedStates);
    const filtered = ESTADOS.filter((e) => set.has(e.sigla));
    return filtered.length ? filtered : ESTADOS;
  }, [allowedStates]);

  const defaultEstado = useMemo(() => {
    // Pre-select if there's a single allowed state
    if (estadosVisiveis.length === 1) return estadosVisiveis[0].sigla;
    return '';
  }, [estadosVisiveis]);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const planRes = await axios.get(`${BACKEND_URL}/api/financeiro/budget`, {
        withCredentials: true,
      });
      setPlan(planRes.data.plan);
    } catch (_err) {
      toast.error('Erro ao carregar orçamento.');
    } finally {
      setLoading(false);
    }
  };

  const update = (patch) => setPlan((p) => ({ ...p, ...patch }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${BACKEND_URL}/api/financeiro/budget`, plan, {
        withCredentials: true,
      });
      setPlan(data.plan);
      toast.success('Orçamento salvo.');
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg =
        typeof detail === 'object' && detail?.errors
          ? detail.errors.join(' · ')
          : typeof detail === 'string'
          ? detail
          : 'Erro ao salvar orçamento.';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const resetDefaults = async () => {
    if (!window.confirm('Resetar orçamento para o padrão? Períodos, áreas e grupos serão recriados.')) return;
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/financeiro/budget/reset`, null, {
        withCredentials: true,
      });
      setPlan(data.plan);
      toast.success('Orçamento resetado.');
    } catch (_err) {
      toast.error('Erro ao resetar.');
    }
  };

  // ---- Sums for live validation ----
  const sums = useMemo(() => {
    if (!plan) return { periods: 0, areas: 0, cities: 0, subareasByArea: {} };
    return {
      periods: sumPct(plan.periods || []),
      areas: sumPct(plan.areas || []),
      cities: sumPct(plan.city_groups || []),
      subareasByArea: Object.fromEntries(
        (plan.areas || []).map((a) => [a.id, sumPct(a.subareas || [])])
      ),
    };
  }, [plan]);

  // ---- Período handlers ----
  const addPeriod = () => {
    update({
      periods: [
        ...(plan.periods || []),
        { id: newId(), name: 'Novo período', data_inicio: '', data_fim: '', percentual: 0 },
      ],
    });
  };
  const updatePeriod = (id, patch) =>
    update({
      periods: plan.periods.map((p) => (p.id === id ? { ...p, ...patch } : p)),
    });
  const removePeriod = (id) =>
    update({ periods: plan.periods.filter((p) => p.id !== id) });

  // ---- Área / Subárea handlers ----
  const addArea = () =>
    update({
      areas: [
        ...(plan.areas || []),
        {
          id: newId(),
          name: 'Nova área',
          cor: '#64748b',
          percentual: 0,
          ordem: plan.areas.length,
          subareas: [],
        },
      ],
    });
  const updateArea = (id, patch) =>
    update({ areas: plan.areas.map((a) => (a.id === id ? { ...a, ...patch } : a)) });
  const removeArea = (id) => update({ areas: plan.areas.filter((a) => a.id !== id) });

  const addSubarea = (areaId) =>
    updateArea(areaId, {
      subareas: [
        ...(plan.areas.find((a) => a.id === areaId).subareas || []),
        { id: newId(), name: 'Nova subárea', percentual: 0, ordem: 0 },
      ],
    });
  const updateSubarea = (areaId, subId, patch) => {
    const area = plan.areas.find((a) => a.id === areaId);
    updateArea(areaId, {
      subareas: area.subareas.map((s) => (s.id === subId ? { ...s, ...patch } : s)),
    });
  };
  const removeSubarea = (areaId, subId) => {
    const area = plan.areas.find((a) => a.id === areaId);
    updateArea(areaId, {
      subareas: area.subareas.filter((s) => s.id !== subId),
    });
  };

  // ---- Cidade / Grupo handlers ----
  const addCityGroup = () =>
    update({
      city_groups: [
        ...(plan.city_groups || []),
        { id: newId(), name: 'Novo grupo', percentual: 0, cidades: [] },
      ],
    });
  const updateCityGroup = (id, patch) =>
    update({
      city_groups: plan.city_groups.map((g) => (g.id === id ? { ...g, ...patch } : g)),
    });
  const removeCityGroup = (id) =>
    update({ city_groups: plan.city_groups.filter((g) => g.id !== id) });

  // Helper accessors for the state+city picker
  const getEstadoOptions = () =>
    estadosVisiveis.map((e) => ({ value: e.sigla, label: `${e.sigla} · ${e.nome}` }));

  const getCidadesForEstado = (estado) => {
    if (!estado || !cidadesBrasil[estado]) return [];
    return cidadesBrasil[estado].map((cidade) => ({ value: cidade, label: cidade }));
  };

  const getDraft = (groupId) => {
    const d = draftSelections[groupId];
    if (d) return d;
    return { estado: defaultEstado, cidade: '' };
  };

  const setDraft = (groupId, patch) => {
    setDraftSelections((prev) => ({
      ...prev,
      [groupId]: { ...getDraft(groupId), ...patch },
    }));
  };

  const addCityToGroup = (groupId) => {
    const draft = getDraft(groupId);
    if (!draft.estado || !draft.cidade) {
      toast.error('Selecione estado e cidade');
      return;
    }
    const group = plan.city_groups.find((g) => g.id === groupId);
    const exists = (group.cidades || []).some(
      (c) => c.estado === draft.estado && c.cidade === draft.cidade
    );
    if (exists) {
      toast.warning('Cidade já adicionada nesse grupo');
      return;
    }
    updateCityGroup(groupId, {
      cidades: [...(group.cidades || []), { estado: draft.estado, cidade: draft.cidade }],
    });
    setDraft(groupId, { cidade: '' });
  };

  const removeCityFromGroup = (groupId, estado, cidade) => {
    const group = plan.city_groups.find((g) => g.id === groupId);
    updateCityGroup(groupId, {
      cidades: group.cidades.filter((c) => !(c.estado === estado && c.cidade === cidade)),
    });
  };

  if (loading || !plan) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="financeiro-planejamento-page">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="h-12 w-12 rounded-xl bg-emerald-100 flex items-center justify-center shrink-0">
              <PiggyBank className="h-6 w-6 text-emerald-700" />
            </div>
            <div>
              <h1 className="text-3xl font-black tracking-tight">Planejamento Orçamentário</h1>
              <p className="text-muted-foreground mt-1 max-w-2xl">
                Defina o orçamento global, distribuição por período, áreas/subáreas e grupos de cidades.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={resetDefaults} data-testid="reset-budget-btn">
              <RotateCcw className="h-3.5 w-3.5 mr-1.5" /> Resetar
            </Button>
            <Button onClick={save} disabled={saving} data-testid="save-budget-btn">
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Salvando...' : 'Salvar orçamento'}
            </Button>
          </div>
        </div>

        {/* Cabeçalho do orçamento */}
        <Card>
          <CardContent className="pt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="md:col-span-2">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                Nome do orçamento
              </Label>
              <Input
                value={plan.name}
                onChange={(e) => update({ name: e.target.value })}
                data-testid="budget-name-input"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                Valor total (R$)
              </Label>
              <Input
                type="number"
                min="0"
                step="100"
                value={plan.valor_total || 0}
                onChange={(e) =>
                  update({ valor_total: parseFloat(e.target.value) || 0 })
                }
                data-testid="budget-total-input"
              />
              <p className="text-xs text-muted-foreground mt-1">{fmtBRL(plan.valor_total)}</p>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                Status
              </Label>
              <Select
                value={plan.status}
                onValueChange={(v) => update({ status: v })}
              >
                <SelectTrigger data-testid="budget-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Rascunho</SelectItem>
                  <SelectItem value="active">Ativo</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">
                {plan.status === 'active'
                  ? 'Validação estrita ao salvar.'
                  : 'Salva mesmo se incompleto.'}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Resumo de validações */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="validation-summary">
          <SummaryRow
            icon={CalendarDays}
            label="Períodos"
            value={sums.periods}
            count={plan.periods.length}
          />
          <SummaryRow
            icon={Layers}
            label="Áreas"
            value={sums.areas}
            count={plan.areas.length}
          />
          <SummaryRow
            icon={MapPin}
            label="Cidades / Grupos"
            value={sums.cities}
            count={plan.city_groups.length}
            optional
          />
        </div>

        {/* Tabs */}
        <Tabs defaultValue="periodos" className="w-full">
          <TabsList>
            <TabsTrigger value="periodos" data-testid="tab-periodos">
              <CalendarDays className="h-3.5 w-3.5 mr-1.5" /> Períodos
            </TabsTrigger>
            <TabsTrigger value="areas" data-testid="tab-areas">
              <Layers className="h-3.5 w-3.5 mr-1.5" /> Áreas e Subáreas
            </TabsTrigger>
            <TabsTrigger value="cidades" data-testid="tab-cidades">
              <MapPin className="h-3.5 w-3.5 mr-1.5" /> Cidades / Grupos
            </TabsTrigger>
          </TabsList>

          {/* PERÍODOS */}
          <TabsContent value="periodos" className="mt-4">
            <Card>
              <CardContent className="pt-6 space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-bold">Períodos da campanha</h3>
                    <p className="text-xs text-muted-foreground">
                      Defina intervalos personalizados. A soma das porcentagens deve ser 100%.
                    </p>
                  </div>
                  <Button size="sm" onClick={addPeriod} data-testid="add-period-btn">
                    <Plus className="h-3.5 w-3.5 mr-1.5" /> Adicionar período
                  </Button>
                </div>

                {plan.periods.length === 0 ? (
                  <p className="text-center text-sm text-muted-foreground py-8">
                    Nenhum período cadastrado.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {plan.periods.map((p) => (
                      <div
                        key={p.id}
                        className="grid grid-cols-12 gap-2 items-end p-3 border border-border rounded-lg"
                        data-testid={`period-row-${p.id}`}
                      >
                        <div className="col-span-12 md:col-span-4">
                          <Label className="text-xs text-muted-foreground">Nome</Label>
                          <Input
                            value={p.name}
                            onChange={(e) => updatePeriod(p.id, { name: e.target.value })}
                          />
                        </div>
                        <div className="col-span-6 md:col-span-3">
                          <Label className="text-xs text-muted-foreground">Início</Label>
                          <Input
                            type="date"
                            value={p.data_inicio || ''}
                            onChange={(e) =>
                              updatePeriod(p.id, { data_inicio: e.target.value })
                            }
                          />
                        </div>
                        <div className="col-span-6 md:col-span-3">
                          <Label className="text-xs text-muted-foreground">Fim</Label>
                          <Input
                            type="date"
                            value={p.data_fim || ''}
                            onChange={(e) =>
                              updatePeriod(p.id, { data_fim: e.target.value })
                            }
                          />
                        </div>
                        <div className="col-span-9 md:col-span-1">
                          <Label className="text-xs text-muted-foreground">%</Label>
                          <Input
                            type="number"
                            min="0"
                            max="100"
                            step="0.5"
                            value={p.percentual}
                            onChange={(e) =>
                              updatePeriod(p.id, { percentual: parseFloat(e.target.value) || 0 })
                            }
                          />
                        </div>
                        <div className="col-span-3 md:col-span-1 flex justify-end">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-destructive"
                            onClick={() => removePeriod(p.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                        <div className="col-span-12 text-xs text-muted-foreground">
                          Equivale a <strong>{fmtBRL((plan.valor_total * (p.percentual || 0)) / 100)}</strong>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ÁREAS */}
          <TabsContent value="areas" className="mt-4">
            <Card>
              <CardContent className="pt-6 space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-bold">Áreas e subáreas da campanha</h3>
                    <p className="text-xs text-muted-foreground">
                      A soma das áreas deve ser 100%. Subáreas (dentro de cada área) também
                      devem somar 100% quando configuradas.
                    </p>
                  </div>
                  <Button size="sm" onClick={addArea} data-testid="add-area-btn">
                    <Plus className="h-3.5 w-3.5 mr-1.5" /> Adicionar área
                  </Button>
                </div>

                <div className="space-y-2">
                  {plan.areas.map((a) => {
                    const subSum = sums.subareasByArea[a.id] || 0;
                    const open = expandedArea === a.id;
                    return (
                      <div
                        key={a.id}
                        className="border border-border rounded-lg overflow-hidden"
                        data-testid={`area-row-${a.id}`}
                      >
                        <div
                          className="grid grid-cols-12 gap-2 items-center p-3"
                          style={{ borderLeft: `4px solid ${a.cor}` }}
                        >
                          <div className="col-span-12 md:col-span-5">
                            <Input
                              value={a.name}
                              onChange={(e) => updateArea(a.id, { name: e.target.value })}
                              className="font-semibold"
                            />
                          </div>
                          <div className="col-span-3 md:col-span-2">
                            <Input
                              type="color"
                              value={a.cor}
                              onChange={(e) => updateArea(a.id, { cor: e.target.value })}
                              className="h-9 p-1 cursor-pointer"
                              title="Cor"
                            />
                          </div>
                          <div className="col-span-5 md:col-span-2">
                            <Input
                              type="number"
                              min="0"
                              max="100"
                              step="0.5"
                              value={a.percentual}
                              onChange={(e) =>
                                updateArea(a.id, {
                                  percentual: parseFloat(e.target.value) || 0,
                                })
                              }
                            />
                          </div>
                          <div className="col-span-3 md:col-span-2 text-xs text-muted-foreground text-right">
                            {fmtBRL((plan.valor_total * (a.percentual || 0)) / 100)}
                          </div>
                          <div className="col-span-1 flex justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => setExpandedArea(open ? null : a.id)}
                              title="Subáreas"
                            >
                              {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-destructive"
                              onClick={() => removeArea(a.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>

                        {open && (
                          <div className="border-t border-border bg-muted/30 p-3 space-y-2">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2 text-xs">
                                <span className="text-muted-foreground">Subáreas:</span>
                                <StatusBadge value={subSum} />
                              </div>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => addSubarea(a.id)}
                                data-testid={`add-subarea-${a.id}`}
                              >
                                <Plus className="h-3.5 w-3.5 mr-1.5" /> Subárea
                              </Button>
                            </div>
                            {a.subareas.length === 0 ? (
                              <p className="text-xs text-muted-foreground text-center py-3">
                                Sem subáreas cadastradas.
                              </p>
                            ) : (
                              a.subareas.map((s) => (
                                <div
                                  key={s.id}
                                  className="grid grid-cols-12 gap-2 items-center"
                                >
                                  <div className="col-span-12 md:col-span-7">
                                    <Input
                                      value={s.name}
                                      onChange={(e) =>
                                        updateSubarea(a.id, s.id, { name: e.target.value })
                                      }
                                    />
                                  </div>
                                  <div className="col-span-7 md:col-span-2">
                                    <Input
                                      type="number"
                                      min="0"
                                      max="100"
                                      step="0.5"
                                      value={s.percentual}
                                      onChange={(e) =>
                                        updateSubarea(a.id, s.id, {
                                          percentual: parseFloat(e.target.value) || 0,
                                        })
                                      }
                                    />
                                  </div>
                                  <div className="col-span-4 md:col-span-2 text-xs text-muted-foreground text-right">
                                    {fmtBRL(
                                      ((plan.valor_total *
                                        (a.percentual || 0) *
                                        (s.percentual || 0)) /
                                        10000)
                                    )}
                                  </div>
                                  <div className="col-span-1 flex justify-end">
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="text-destructive"
                                      onClick={() => removeSubarea(a.id, s.id)}
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </div>
                                </div>
                              ))
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* CIDADES */}
          <TabsContent value="cidades" className="mt-4">
            <Card>
              <CardContent className="pt-6 space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-bold">Grupos de cidades</h3>
                    <p className="text-xs text-muted-foreground">
                      Distribua o orçamento por grupo de cidades (opcional). Quando configurado,
                      a soma deve ser 100%.
                    </p>
                  </div>
                  <Button size="sm" onClick={addCityGroup} data-testid="add-citygroup-btn">
                    <Plus className="h-3.5 w-3.5 mr-1.5" /> Adicionar grupo
                  </Button>
                </div>
                {plan.city_groups.length === 0 ? (
                  <p className="text-center text-sm text-muted-foreground py-8">
                    Nenhum grupo de cidades cadastrado.
                  </p>
                ) : (
                  plan.city_groups.map((g) => (
                    <div
                      key={g.id}
                      className="border border-border rounded-lg p-3 space-y-3"
                      data-testid={`citygroup-row-${g.id}`}
                    >
                      <div className="grid grid-cols-12 gap-2 items-end">
                        <div className="col-span-12 md:col-span-6">
                          <Label className="text-xs text-muted-foreground">Nome do grupo</Label>
                          <Input
                            value={g.name}
                            onChange={(e) => updateCityGroup(g.id, { name: e.target.value })}
                          />
                        </div>
                        <div className="col-span-6 md:col-span-2">
                          <Label className="text-xs text-muted-foreground">%</Label>
                          <Input
                            type="number"
                            min="0"
                            max="100"
                            step="0.5"
                            value={g.percentual}
                            onChange={(e) =>
                              updateCityGroup(g.id, {
                                percentual: parseFloat(e.target.value) || 0,
                              })
                            }
                          />
                        </div>
                        <div className="col-span-5 md:col-span-3 text-xs text-muted-foreground">
                          {fmtBRL((plan.valor_total * (g.percentual || 0)) / 100)}
                        </div>
                        <div className="col-span-1 flex justify-end">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-destructive"
                            onClick={() => removeCityGroup(g.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">Cidades</Label>
                        <div className="flex flex-wrap gap-1 mb-2">
                          {(g.cidades || []).length === 0 && (
                            <span className="text-xs text-muted-foreground">
                              Nenhuma cidade adicionada.
                            </span>
                          )}
                          {(g.cidades || []).map((c) => (
                            <Badge
                              key={`${c.estado}-${c.cidade}`}
                              variant="secondary"
                              className="gap-1.5"
                            >
                              {c.cidade}
                              {c.estado ? ` / ${c.estado}` : ''}
                              <button
                                onClick={() => removeCityFromGroup(g.id, c.estado, c.cidade)}
                                className="hover:text-destructive"
                                title="Remover"
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </Badge>
                          ))}
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-12 gap-2 items-end">
                          <div className="md:col-span-4">
                            <Label className="text-xs text-muted-foreground">Estado</Label>
                            <Combobox
                              value={getDraft(g.id).estado}
                              onChange={(v) => setDraft(g.id, { estado: v || '', cidade: '' })}
                              options={getEstadoOptions()}
                              placeholder="Selecione o estado"
                              searchPlaceholder="Buscar estado..."
                              emptyLabel="Sem estados disponíveis"
                              testId={`add-estado-${g.id}`}
                            />
                          </div>
                          <div className="md:col-span-6">
                            <Label className="text-xs text-muted-foreground">Cidade</Label>
                            <Combobox
                              value={getDraft(g.id).cidade}
                              onChange={(v) => setDraft(g.id, { cidade: v || '' })}
                              options={getCidadesForEstado(getDraft(g.id).estado)}
                              placeholder={
                                getDraft(g.id).estado
                                  ? 'Selecione a cidade'
                                  : 'Selecione o estado primeiro'
                              }
                              searchPlaceholder="Buscar cidade..."
                              emptyLabel="Sem cidades disponíveis"
                              testId={`add-cidade-${g.id}`}
                            />
                          </div>
                          <div className="md:col-span-2">
                            <Button
                              type="button"
                              size="sm"
                              onClick={() => addCityToGroup(g.id)}
                              disabled={!getDraft(g.id).estado || !getDraft(g.id).cidade}
                              className="w-full"
                              data-testid={`add-city-btn-${g.id}`}
                            >
                              <Plus className="h-3.5 w-3.5 mr-1.5" /> Adicionar
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

const SummaryRow = ({ icon: Icon, label, value, count, optional }) => {
  const diff = Math.abs((value || 0) - 100);
  const ok = diff <= 0.01;
  return (
    <Card>
      <CardContent className="pt-5">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Icon className="h-4 w-4 text-muted-foreground" />
            <span className="text-xs uppercase tracking-wider text-muted-foreground font-bold">
              {label}
            </span>
            <Badge variant="outline" className="text-xs">{count}</Badge>
          </div>
          {ok ? (
            <CheckCircle2 className="h-4 w-4 text-emerald-600" />
          ) : optional && count === 0 ? (
            <span className="text-xs text-muted-foreground">opcional</span>
          ) : (
            <AlertCircle className="h-4 w-4 text-amber-600" />
          )}
        </div>
        <div className="flex items-center justify-between mb-2">
          <span className="text-2xl font-bold">{value}%</span>
          <StatusBadge value={value} />
        </div>
        <ProgressBar value={value} />
      </CardContent>
    </Card>
  );
};
